import type {
    FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type ISwipeableRowActionProps from "./type";
import stylesheet, {
    getSwipeableRowActionAlign,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import {
    getStateColorKey
} from "../../../../helpers/theme/state";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const SwipeableRowAction: FC<ISwipeableRowActionProps> = ({
    isFocusable = true,
    customTheme,
    isShowLabel,
    isFirst,
    onPress,
    action,
    isFull,
    width,
    side
}) => {
    const {
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({});

    const type = action.type ?? "neutral";

    const currentColors = getTypeColors({
        variant: type === "danger" ? "filled" : "tonal",
        colors,
        type
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        content: contentDynamicStyle
    } = useStyles({
        contentAlign: getSwipeableRowActionAlign({
            isFirst,
            isFull,
            side
        }),
        isCollapsed: isFull && !isFirst,
        colorKey: getStateColorKey({
            type
        }),
        isFocusVisible,
        currentColors,
        borders,
        colors,
        spaces,
        width
    });

    return <Pressable
        {...interactionHandlers}
        onPress={() => onPress(action)}
        tabIndex={isFocusable ? 0 : -1}
        aria-label={action.title}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="button"
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                overlayColor={currentColors.overlayColor}
                customTheme={customTheme}
                isPressed={isPressed}
                isHovered={isHovered}
                borderRadius={0}
            />
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                <action.icon
                    customColor={currentColors.iconColor}
                    size={iconSizes.large}
                    color="default"
                />
                {
                    isShowLabel ?
                        <Text
                            customColor={currentColors.titleColor}
                            customTheme={customTheme}
                            style={stylesheet.label}
                            variant="labelSmallSize"
                            numberOfLines={1}
                        >
                            {action.title}
                        </Text>
                    :
                        null
                }
            </View>
        </View>
    </Pressable>;
};
export default SwipeableRowAction;
